import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Filter, MapPin, User, Calendar as CalendarIcon, X, Building2, Clock, MessageSquare, ChevronRight, Pencil, Save, Trash2, Wifi, Eye, EyeOff, Plus, Download, Cable, Signal } from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";


import { CATEGORIES, PARENT_CATEGORIES, subcategoriesFor } from "@/constants/categories";

export default function OfflineLogs() {
  const { user } = useAuth();
  const isInternal = user?.user_type === "internal";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedDDCode, setSelectedDDCode] = useState(null);
  const [ddCodeInfo, setDdCodeInfo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Date filter state (independent of DD_Code search)
  const [selectedDate, setSelectedDate] = useState("");
  const [dateFilteredLogs, setDateFilteredLogs] = useState([]);
  const [dateFilterLoading, setDateFilterLoading] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({
    category: "",
    installer: "",
    dolphin_manager: "",
    detailed_comments: "",
    wifi_name: "",
    wifi_password: ""
  });
  const [saving, setSaving] = useState(false);
  
  // Password visibility state for timeline
  const [visiblePasswords, setVisiblePasswords] = useState({});
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingLog, setDeletingLog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [filters, setFilters] = useState({
    category: "",
    installer: "",
    dolphin_manager: "",
    date: "",
    date_from: null,
    date_to: null
  });
  const [filterOptions, setFilterOptions] = useState({
    installers: [],
    dolphin_managers: [],
    pas: [],
    dates: []
  });
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Top search filters state
  const [topFilters, setTopFilters] = useState({
    categories: [],
    parent_categories: [],
    installers: [],
    dolphin_managers: [],
    pas: [],
    date_from: null,
    date_to: null
  });
  const [topDateFromOpen, setTopDateFromOpen] = useState(false);
  const [topDateToOpen, setTopDateToOpen] = useState(false);
  const [userCleared, setUserCleared] = useState(false);
  const [deviceInfoLookup, setDeviceInfoLookup] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/offline-analytics/device-info`, { withCredentials: true });
        if (!cancelled) {
          setDeviceInfoLookup(res.data?.lookup || {});
        }
      } catch (error) {
        console.error('Error loading device info data:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const ddFromUrl = searchParams.get('dd');
    if (ddFromUrl && !selectedDDCode && !userCleared) {
      setSelectedDDCode(ddFromUrl);
      setSearchQuery(ddFromUrl);
    }
    if (!ddFromUrl) {
      setUserCleared(false);
    }
  }, [searchParams, selectedDDCode, userCleared]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters`);
      
      setFilterOptions({
        installers: response.data.installers,
        dolphin_managers: response.data.dolphin_managers,
        pas: response.data.pas || [],
        dates: response.data.dates || []
      });
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const searchDDCodes = async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/logs/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error("Error searching:", error);
    }
  };

  // Fetch logs by top filters (independent filter)
  const fetchLogsByTopFilters = useCallback(async () => {
    const hasTopFilters = selectedDate || topFilters.categories.length > 0 || topFilters.parent_categories.length > 0 || topFilters.installers.length > 0 || topFilters.dolphin_managers.length > 0 || topFilters.pas.length > 0 || topFilters.date_from || topFilters.date_to;
    
    if (!hasTopFilters) {
      setDateFilteredLogs([]);
      return;
    }
    
    setDateFilterLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Add selected date filter
      if (selectedDate) {
        params.append("date_from", selectedDate);
        params.append("date_to", selectedDate);
      } else {
        // Add date range from top filters
        if (topFilters.date_from) params.append("date_from", format(topFilters.date_from, "yyyy-MM-dd"));
        if (topFilters.date_to) params.append("date_to", format(topFilters.date_to, "yyyy-MM-dd"));
      }
      
      const catLeaves = topFilters.categories.length > 0
        ? topFilters.categories
        : topFilters.parent_categories.flatMap(p => subcategoriesFor(p));
      if (catLeaves.length > 0) params.append("category", catLeaves.join(","));
      if (topFilters.installers.length > 0) params.append("installer", topFilters.installers.join(","));
      if (topFilters.dolphin_managers.length > 0) params.append("dolphin_manager", topFilters.dolphin_managers.join(","));
      if (topFilters.pas.length > 0) params.append("pa", topFilters.pas.join(","));
      
      params.append("page_size", "100");
      
      const response = await axios.get(`${API}/logs?${params.toString()}`);
      setDateFilteredLogs(response.data.logs);
    } catch (error) {
      console.error("Error fetching logs by filters:", error);
      toast.error("Failed to fetch logs with these filters");
    } finally {
      setDateFilterLoading(false);
    }
  }, [selectedDate, topFilters]);

  const handleTopFilterChange = (field, value) => {
    setTopFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearTopFilters = () => {
    setTopFilters({
      categories: [],
      parent_categories: [],
      installers: [],
      dolphin_managers: [],
      pas: [],
      date_from: null,
      date_to: null
    });
    setSelectedDate("");
    setDateFilteredLogs([]);
  };

  const hasActiveTopFilters = selectedDate || topFilters.categories.length > 0 || topFilters.parent_categories.length > 0 || topFilters.installers.length > 0 || topFilters.dolphin_managers.length > 0 || topFilters.pas.length > 0 || topFilters.date_from || topFilters.date_to;

  // Download filtered logs as CSV
  const downloadFilteredLogsCSV = async () => {
    const hasTopFilters = selectedDate || topFilters.categories.length > 0 || topFilters.parent_categories.length > 0 || topFilters.installers.length > 0 || topFilters.dolphin_managers.length > 0 || topFilters.pas.length > 0 || topFilters.date_from || topFilters.date_to;
    
    if (!hasTopFilters) {
      toast.error("No filters applied. Please apply filters first.");
      return;
    }

    try {
      const params = new URLSearchParams();
      if (selectedDate) {
        params.append("date_from", selectedDate);
        params.append("date_to", selectedDate);
      } else {
        if (topFilters.date_from) params.append("date_from", format(topFilters.date_from, "yyyy-MM-dd"));
        if (topFilters.date_to) params.append("date_to", format(topFilters.date_to, "yyyy-MM-dd"));
      }
      const catLeaves = topFilters.categories.length > 0
        ? topFilters.categories
        : topFilters.parent_categories.flatMap(p => subcategoriesFor(p));
      if (catLeaves.length > 0) params.append("category", catLeaves.join(","));
      if (topFilters.installers.length > 0) params.append("installer", topFilters.installers.join(","));
      if (topFilters.dolphin_managers.length > 0) params.append("dolphin_manager", topFilters.dolphin_managers.join(","));
      if (topFilters.pas.length > 0) params.append("pa", topFilters.pas.join(","));

      toast.info("Fetching all logs for download...");
      
      // Paginate through all results
      let allLogs = [];
      let page = 1;
      let totalPages = 1;
      const pageSize = 100;
      
      do {
        const pageParams = new URLSearchParams(params);
        pageParams.append("page", String(page));
        pageParams.append("page_size", String(pageSize));
        const response = await axios.get(`${API}/logs?${pageParams.toString()}`);
        allLogs = allLogs.concat(response.data.logs);
        totalPages = response.data.total_pages;
        page++;
      } while (page <= totalPages);

      const logsToDownload = allLogs;

      if (logsToDownload.length === 0) {
        toast.error("No logs to download");
        return;
      }
    
    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      // If value contains comma, newline, or quotes, wrap in quotes and escape existing quotes
      if (str.includes(',') || str.includes('\n') || str.includes('"') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };
    
    const headers = [
      "Date",
      "DD Code",
      "Venue Name",
      "Store Name",
      "Address",
      "PA",
      "Category",
      "Installer",
      "Dolphin Manager",
      "Comments"
    ];
    
    const rows = logsToDownload.map(log => [
      escapeCSV(log.date),
      escapeCSV(log.dd_code),
      escapeCSV(log.venue_name),
      escapeCSV(log.store_name),
      escapeCSV(log.address),
      escapeCSV(log.pa),
      escapeCSV(log.category),
      escapeCSV(log.installer),
      escapeCSV(log.dolphin_manager),
      escapeCSV(log.detailed_comments)
    ]);
    
    const csvContent = [
      headers.map(h => escapeCSV(h)).join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");
    
    // Generate filename based on filters
    let filename = "offline_logs";
    if (selectedDate) filename += `_${selectedDate}`;
    if (topFilters.category) filename += `_${topFilters.category.replace(/\s+/g, "_")}`;
    if (topFilters.installer) filename += `_${topFilters.installer.replace(/\s+/g, "_")}`;
    if (topFilters.dolphin_manager) filename += `_${topFilters.dolphin_manager.replace(/\s+/g, "_")}`;
    filename += ".csv";
    
    // Download file with BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
    
    toast.success(`Downloaded ${logsToDownload.length} logs`);
    } catch (error) {
      console.error("Error downloading logs:", error);
      toast.error("Failed to download logs");
    }
  };

  // Download DD Code logs as CSV
  const downloadDDCodeLogsCSV = () => {
    if (logs.length === 0) {
      toast.error("No logs to download");
      return;
    }
    
    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '""';
      const str = String(value);
      // If value contains comma, newline, or quotes, wrap in quotes and escape existing quotes
      if (str.includes(',') || str.includes('\n') || str.includes('"') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };
    
    // Define CSV headers (WiFi credentials excluded for security)
    const headers = [
      "Date",
      "DD Code",
      "Venue Name",
      "Store Name",
      "Address",
      "PA",
      "Category",
      "Installer",
      "Dolphin Manager",
      "Comments"
    ];
    
    // Build CSV rows with proper escaping
    const rows = logs.map(log => [
      escapeCSV(log.date),
      escapeCSV(log.dd_code),
      escapeCSV(log.venue_name),
      escapeCSV(log.store_name),
      escapeCSV(log.address),
      escapeCSV(log.pa),
      escapeCSV(log.category),
      escapeCSV(log.installer),
      escapeCSV(log.dolphin_manager),
      escapeCSV(log.detailed_comments)
    ]);
    
    // Create CSV content
    const csvContent = [
      headers.map(h => escapeCSV(h)).join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");
    
    // Generate filename
    const filename = `offline_logs_${selectedDDCode}.csv`;
    
    // Download file with BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
    
    toast.success(`Downloaded ${logs.length} logs for ${selectedDDCode}`);
  };

  const fetchDDCodeHistory = useCallback(async (ddCode) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append("category", filters.category);
      if (filters.installer) params.append("installer", filters.installer);
      if (filters.dolphin_manager) params.append("dolphin_manager", filters.dolphin_manager);
      if (filters.date) {
        params.append("date_from", filters.date);
        params.append("date_to", filters.date);
      } else {
        if (filters.date_from) params.append("date_from", format(filters.date_from, "yyyy-MM-dd"));
        if (filters.date_to) params.append("date_to", format(filters.date_to, "yyyy-MM-dd"));
      }

      const response = await axios.get(`${API}/logs/dd/${encodeURIComponent(ddCode)}?${params.toString()}`);
      setDdCodeInfo(response.data.info);
      setLogs(response.data.logs);
    } catch (error) {
      console.error("Error fetching DD Code history:", error);
      if (error.response && error.response.status === 404) {
        // Use CSV data as fallback when no logs exist
        const csvInfo = deviceInfoLookup[ddCode] || {};
        setDdCodeInfo({
          dd_code: ddCode,
          venue_name: csvInfo.storeName || "Unknown",
          store_name: csvInfo.storeName || "Unknown",
          address: csvInfo.address || "No address found",
          pa: csvInfo.pa || "",
          fa: csvInfo.fa || "",
          stick_number: csvInfo.serialNumber || "",
          network_type: csvInfo.networkType || "",
          owner_name: csvInfo.partnerName || "",
          contact_number: csvInfo.phoneNumber || "",
          owner_email: csvInfo.email || "",
          // Additional CSV info
          state: csvInfo.state || "",
          city: csvInfo.city || "",
          os_version: csvInfo.osVersion || "",
          stick_model: csvInfo.stickModel || "",
          offline_days: csvInfo.offlineDays || "",
          status: csvInfo.status || "",
        });
        setLogs([]);
      } else {
        toast.error("Failed to fetch DD Code history");
      }
    } finally {
      setLoading(false);
    }
  }, [filters, deviceInfoLookup]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (selectedDDCode) {
      fetchDDCodeHistory(selectedDDCode);
    }
  }, [selectedDDCode, fetchDDCodeHistory]);

  useEffect(() => {
    if (selectedDDCode && ddCodeInfo && ddCodeInfo.venue_name === "Unknown" && deviceInfoLookup[selectedDDCode]) {
      const csvInfo = deviceInfoLookup[selectedDDCode];
      setDdCodeInfo(prev => ({
        ...prev,
        venue_name: csvInfo.storeName || prev.venue_name,
        store_name: csvInfo.storeName || prev.store_name,
        address: csvInfo.address || prev.address,
        pa: csvInfo.pa || prev.pa,
        fa: csvInfo.fa || prev.fa,
        stick_number: csvInfo.serialNumber || prev.stick_number,
        network_type: csvInfo.networkType || prev.network_type,
        owner_name: csvInfo.partnerName || prev.owner_name,
        contact_number: csvInfo.phoneNumber || prev.contact_number,
        owner_email: csvInfo.email || prev.owner_email,
        state: csvInfo.state || prev.state,
        city: csvInfo.city || prev.city,
      }));
    }
  }, [selectedDDCode, ddCodeInfo, deviceInfoLookup]);


  useEffect(() => {
    const debounce = setTimeout(() => {
      searchDDCodes(searchQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  useEffect(() => {
    const hasTopFilters = selectedDate || topFilters.categories.length > 0 || topFilters.parent_categories.length > 0 || topFilters.installers.length > 0 || topFilters.dolphin_managers.length > 0 || topFilters.pas.length > 0 || topFilters.date_from || topFilters.date_to;
    
    if (hasTopFilters && !selectedDDCode) {
      fetchLogsByTopFilters();
    } else if (!selectedDDCode) {
      setDateFilteredLogs([]);
    }
  }, [selectedDate, topFilters, selectedDDCode, fetchLogsByTopFilters]);

  const handleSelectDDCode = (result) => {
    setSelectedDDCode(result.dd_code);
    setSearchQuery(result.dd_code);
    setShowSearchResults(false);
    setSearchResults([]);
    setSelectedDate(""); 
    setDateFilteredLogs([]);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      installer: "",
      dolphin_manager: "",
      date: "",
      date_from: null,
      date_to: null
    });
  };

  const clearSelection = () => {
    setUserCleared(true);
    setSelectedDDCode(null);
    setDdCodeInfo(null);
    setLogs([]);
    setSearchQuery("");
    clearFilters();
    // Clear URL parameter
    setSearchParams({});
  };

  // Edit functionality
  const openEditModal = (log) => {
    setEditingLog(log);
    setEditForm({
      category: log.category || "",
      installer: log.installer || "",
      dolphin_manager: log.dolphin_manager || "",
      detailed_comments: log.detailed_comments || "",
      network_type: log.network_type || "",
      wifi_name: log.wifi_name || "",
      wifi_password: log.wifi_password || "",
      wifi_autoconnect: log.wifi_autoconnect || "",
      latest_apk: log.latest_apk || "",
      kiosk_mode: log.kiosk_mode || "",
      // Priority: database FA first, then CSV lookup as fallback
      fa: log.fa || deviceInfoLookup[log.dd_code]?.fa || "",
      // Owner contact info
      contact_number: log.contact_number || "",
      owner_name: log.owner_name || "",
      owner_email: log.owner_email || ""
    });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (logId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const saveEdit = async () => {
    if (!editingLog) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/logs/${editingLog.id}`, editForm);
      toast.success("Log updated successfully");
      setEditModalOpen(false);
      
      // Refresh the logs
      if (selectedDDCode) {
        fetchDDCodeHistory(selectedDDCode);
      }
      if (hasActiveTopFilters && !selectedDDCode) {
        fetchLogsByTopFilters();
      }
      
      fetchFilterOptions();
    } catch (error) {
      console.error("Error updating log:", error);
      toast.error("Failed to update log");
    } finally {
      setSaving(false);
    }
  };

  // Delete functionality
  const openDeleteDialog = (log) => {
    setDeletingLog(log);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingLog) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/logs/${deletingLog.id}`);
      toast.success("Log deleted successfully");
      setDeleteDialogOpen(false);
      setDeletingLog(null);
      setLogs(prevLogs => prevLogs.filter(log => log.id !== deletingLog.id));
      setSearchResults([]);
      
      setDateFilteredLogs(prevLogs => prevLogs.filter(log => log.id !== deletingLog.id));
      
      // Refresh the logs from server
      if (selectedDDCode) {
        fetchDDCodeHistory(selectedDDCode);
      }
      if (hasActiveTopFilters && !selectedDDCode) {
        fetchLogsByTopFilters();
      }
      
      fetchFilterOptions();
    } catch (error) {
      console.error("Error deleting log:", error);
      toast.error("Failed to delete log");
    } finally {
      setDeleting(false);
    }
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      "No Issue Found": "bg-emerald-100 text-emerald-700 border-emerald-200",
      "Dont want screen": "bg-red-100 text-red-700 border-red-200",
      "Permanently closed": "bg-red-100 text-red-700 border-red-200",
      "Store closed": "bg-orange-100 text-orange-700 border-orange-200",
      "Temporarily closed": "bg-orange-100 text-orange-700 border-orange-200",
      "Power disconnected": "bg-yellow-100 text-yellow-700 border-yellow-200",
      "Power Supply issue": "bg-yellow-100 text-yellow-700 border-yellow-200",
      "Wifi Disconnects itself": "bg-blue-100 text-blue-700 border-blue-200",
      "Store Wifi changed": "bg-blue-100 text-blue-700 border-blue-200",
      "Weak WIFI Connection": "bg-blue-100 text-blue-700 border-blue-200",
      "TMV not working": "bg-purple-100 text-purple-700 border-purple-200",
      "TMV updated": "bg-emerald-100 text-emerald-700 border-emerald-200",
      "Screen malfunction": "bg-pink-100 text-pink-700 border-pink-200",
      "Screen & Stick replaced": "bg-emerald-100 text-emerald-700 border-emerald-200",
    };
    return colorMap[category] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const hasActiveFilters = filters.category || filters.installer || filters.dolphin_manager || filters.date || filters.date_from || filters.date_to;

  const renderLogCard = (log, index, showDDCode = false) => (
    <div key={log.id} className="relative pb-8 last:pb-0 animate-fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
      {/* Timeline dot */}
      <div className={`timeline-dot ${index === 0 ? '' : 'timeline-dot-inactive'}`}></div>
      
      <div className="dolphin-card p-5 ml-6 hover-lift">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{log.date}</span>
            {showDDCode && (
              <button 
                onClick={() => handleSelectDDCode({ dd_code: log.dd_code, venue_name: log.venue_name, store_name: log.store_name, address: log.address })}
                className="text-sm font-semibold text-[#006994] hover:underline"
              >
                {log.dd_code}
              </button>
            )}
            <Badge className={`${getCategoryColor(log.category)} border font-medium`}>
              {log.category}
            </Badge>
          </div>
          {isInternal && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditModal(log)}
              className="text-slate-400 hover:text-[#006994] rounded-lg"
              data-testid={`edit-log-${log.id}`}
              aria-label="Edit log"
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeleteDialog(log)}
              className="text-slate-400 hover:text-red-500 rounded-lg"
              data-testid={`delete-log-${log.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          )}
        </div>
        
        {showDDCode && (
          <div className="mb-4 text-sm text-slate-600">
            <span className="font-medium">{log.venue_name}</span> • {log.store_name}
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
              <User className="w-4 h-4 text-slate-500" />
            </div>
            <div>
              <span className="text-xs text-slate-400">Installer</span>
              <p className="text-sm font-medium text-slate-900">{log.installer || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
              <User className="w-4 h-4 text-slate-500" />
            </div>
            <div>
              <span className="text-xs text-slate-400">Dolphin Manager</span>
              <p className="text-sm font-medium text-slate-900">{log.dolphin_manager || '-'}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <User className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <span className="text-xs text-slate-400">PA</span>
              <p className="text-sm font-medium text-slate-900">{log.pa || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <User className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <span className="text-xs text-slate-400">FA</span>
              <p className="text-sm font-medium text-slate-900">{log.fa || '-'}</p>
            </div>
          </div>
        </div>
        
        {log.detailed_comments && (
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-start gap-3">
              <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5" />
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{log.detailed_comments}</p>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full glass-effect border-b border-slate-100 px-8 py-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 ocean-gradient rounded-xl flex items-center justify-center">
            <Search className="w-6 h-6 text-[#006994]" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline Logs
            </h1>
            <p className="text-sm text-slate-500">Search and view screen history by DD_Code or Date</p>
          </div>
        </div>
      </header>

      <div className="p-8 lg:p-12">
        <div className="max-w-6xl mx-auto mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2 relative">
              <div className="dolphin-card p-2">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    data-testid="search-input"
                    placeholder="Search by DD_Code, Venue Name, Store name, or Address..."
                    value={searchQuery}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setSearchQuery(newValue);
                      if (selectedDDCode && newValue !== selectedDDCode) {
                        setUserCleared(true);
                        setSelectedDDCode(null);
                        setDdCodeInfo(null);
                        setLogs([]);
                        // Clear URL parameter
                        setSearchParams({});
                      }
                      if (newValue) {
                        clearTopFilters();
                        setShowSearchResults(true);
                      }
                    }}
                    onFocus={() => searchQuery && setShowSearchResults(true)}
                    className="pl-12 h-14 text-base border-0 bg-transparent focus:ring-0 focus:ring-offset-0 placeholder:text-slate-400"
                  />
                  {(selectedDDCode || searchQuery) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearSelection}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 rounded-full w-8 h-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              {showSearchResults && searchResults.length > 0 && !selectedDDCode && (
                <div className="absolute top-full left-0 right-0 mt-2 dolphin-card z-[100] max-h-80 overflow-auto animate-fade-in-up">
                  {searchResults.map((result) => (
                    <div
                      key={result.dd_code}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectDDCode(result);
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      className="w-full px-5 py-4 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors flex items-center gap-4 cursor-pointer select-none"
                      data-testid={`search-result-${result.dd_code}`}
                    >
                      <div className="w-10 h-10 rounded-lg ocean-gradient flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-[#006994]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900">{result.dd_code}</div>
                        <div className="text-sm text-slate-500 truncate">{result.venue_name} • {result.store_name}</div>
                        <div className="text-xs text-slate-400 truncate">{result.address}</div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="dolphin-card p-2">
              <div className="relative">
                <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Select
                  value={selectedDate}
                  onValueChange={(value) => {
                    const newDate = value === "all" ? "" : value;
                    setSelectedDate(newDate);
                    if (newDate) {
                      setSelectedDDCode(null);
                      setDdCodeInfo(null);
                      setLogs([]);
                      setSearchQuery("");
                    }
                  }}
                >
                  <SelectTrigger 
                    data-testid="date-filter-main" 
                    className="pl-12 h-14 text-base border-0 bg-transparent focus:ring-0"
                  >
                    <SelectValue placeholder="Current Date" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="all">All dates</SelectItem>
                    {filterOptions.dates.map((date) => (
                      <SelectItem key={date} value={date}>{date}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedDate("")}
                    className="absolute right-10 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 rounded-full w-8 h-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="dolphin-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-[#006994]" />
              <span className="text-sm font-medium text-slate-700">Search Filters</span>
              {hasActiveTopFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearTopFilters}
                  className="text-slate-500 hover:text-slate-700 h-7 ml-auto"
                  data-testid="clear-top-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <MultiSelect
                options={PARENT_CATEGORIES}
                selected={topFilters.parent_categories}
                onChange={(values) => {
                  setTopFilters(prev => {
                    const allowed = new Set(values.flatMap(p => subcategoriesFor(p)));
                    const trimmedSubs = values.length === 0
                      ? prev.categories
                      : prev.categories.filter(c => allowed.has(c));
                    return { ...prev, parent_categories: values, categories: trimmedSubs };
                  });
                  if (values.length > 0) {
                    setSelectedDDCode(null);
                    setDdCodeInfo(null);
                    setLogs([]);
                    setSearchQuery("");
                  }
                }}
                placeholder="Category"
                testId="top-filter-parent-category"
              />

              <MultiSelect
                options={
                  topFilters.parent_categories.length > 0
                    ? topFilters.parent_categories.flatMap(p => subcategoriesFor(p))
                    : CATEGORIES
                }
                selected={topFilters.categories}
                onChange={(values) => {
                  setTopFilters(prev => ({ ...prev, categories: values }));
                  if (values.length > 0) {
                    setSelectedDDCode(null);
                    setDdCodeInfo(null);
                    setLogs([]);
                    setSearchQuery("");
                  }
                }}
                placeholder="Subcategory"
                testId="top-filter-category"
              />

              <MultiSelect
                options={filterOptions.installers}
                selected={topFilters.installers}
                onChange={(values) => {
                  setTopFilters(prev => ({ ...prev, installers: values }));
                  if (values.length > 0) {
                    setSelectedDDCode(null);
                    setDdCodeInfo(null);
                    setLogs([]);
                    setSearchQuery("");
                  }
                }}
                placeholder="Installer"
                testId="top-filter-installer"
              />

              <MultiSelect
                options={filterOptions.dolphin_managers}
                selected={topFilters.dolphin_managers}
                onChange={(values) => {
                  setTopFilters(prev => ({ ...prev, dolphin_managers: values }));
                  if (values.length > 0) {
                    setSelectedDDCode(null);
                    setDdCodeInfo(null);
                    setLogs([]);
                    setSearchQuery("");
                  }
                }}
                placeholder="Manager"
                testId="top-filter-manager"
              />

              <MultiSelect
                options={filterOptions.pas}
                selected={topFilters.pas}
                onChange={(values) => {
                  setTopFilters(prev => ({ ...prev, pas: values }));
                  if (values.length > 0) {
                    setSelectedDDCode(null);
                    setDdCodeInfo(null);
                    setLogs([]);
                    setSearchQuery("");
                  }
                }}
                placeholder="PA"
                testId="top-filter-pa"
              />

              <Popover open={topDateFromOpen} onOpenChange={setTopDateFromOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="top-filter-date-from"
                    className={`h-10 justify-start text-left font-normal text-sm ${!topFilters.date_from && "text-slate-400"}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {topFilters.date_from ? format(topFilters.date_from, "PP") : "From"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={topFilters.date_from}
                    onSelect={(date) => {
                      handleTopFilterChange("date_from", date);
                      setTopDateFromOpen(false);
                      if (date) {
                        setSelectedDDCode(null);
                        setDdCodeInfo(null);
                        setLogs([]);
                        setSearchQuery("");
                        setSelectedDate(""); // Clear current date when using date range
                      }
                    }}
                    disabled={(date) => topFilters.date_to ? date > topFilters.date_to : false}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Popover open={topDateToOpen} onOpenChange={setTopDateToOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="top-filter-date-to"
                    className={`h-10 justify-start text-left font-normal text-sm ${!topFilters.date_to && "text-slate-400"}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {topFilters.date_to ? format(topFilters.date_to, "PP") : "To"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={topFilters.date_to}
                    onSelect={(date) => {
                      handleTopFilterChange("date_to", date);
                      setTopDateToOpen(false);
                      if (date) {
                        setSelectedDDCode(null);
                        setDdCodeInfo(null);
                        setLogs([]);
                        setSearchQuery("");
                        setSelectedDate(""); 
                      }
                    }}
                    disabled={(date) => topFilters.date_from ? date < topFilters.date_from : false}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Filter Results */}
        {hasActiveTopFilters && !selectedDDCode && (
          <div className="animate-fade-in-up">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <CalendarIcon className="w-5 h-5 text-[#006994]" />
                Filtered Results
                {selectedDate && <span className="text-sm font-normal text-slate-500">({selectedDate})</span>}
                {(topFilters.date_from || topFilters.date_to) && (
                  <span className="text-sm font-normal text-slate-500">
                    ({topFilters.date_from ? format(topFilters.date_from, "PP") : "..."} - {topFilters.date_to ? format(topFilters.date_to, "PP") : "..."})
                  </span>
                )}
                <span className="text-sm font-normal text-slate-500">({dateFilteredLogs.length} entries)</span>
              </h3>
              {dateFilteredLogs.length > 0 && (
                <Button
                  onClick={downloadFilteredLogsCSV}
                  variant="outline"
                  size="sm"
                  className="text-[#006994] border-[#006994] hover:bg-[#006994] hover:text-white"
                  data-testid="download-filtered-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
              )}
            </div>

            {dateFilterLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-10 h-10 border-3 border-slate-200 border-t-[#006994] rounded-full animate-spin"></div>
              </div>
            ) : dateFilteredLogs.length === 0 ? (
              <div className="dolphin-card py-16 text-center">
                <div className="w-16 h-16 ocean-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                  <CalendarIcon className="w-8 h-8 text-[#006994]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  No Logs Found
                </h3>
                <p className="text-slate-500">No log entries found matching your filters.</p>
              </div>
            ) : (
              <div className="relative pl-8">
                <div className="timeline-line"></div>
                {dateFilteredLogs.map((log, index) => renderLogCard(log, index, true))}
              </div>
            )}
          </div>
        )}

        {selectedDDCode && ddCodeInfo && (
          <div className="animate-fade-in-up">
            {/* Info Card */}
            <div className="dolphin-card overflow-hidden mb-8">
              <div className="ocean-gradient-dark p-6">
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 text-white">
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {ddCodeInfo.dd_code}
                      </h2>
                      <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                        {logs.length} logs
                      </Badge>
                      <div className="flex items-center gap-2 ml-auto">
                        {logs.length > 0 && (
                          <Button
                            onClick={downloadDDCodeLogsCSV}
                            size="sm"
                            className="bg-white/20 hover:bg-white/30 text-white border-white/30 backdrop-blur-sm"
                            data-testid="download-ddcode-csv-btn"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download CSV
                          </Button>
                        )}
                        {isInternal && (
                        <Button
                          onClick={() => {
                            const logFA = (logs && logs.length > 0 && logs[0].fa) ? logs[0].fa : '';
                            const params = new URLSearchParams({
                              dd_code: ddCodeInfo.dd_code,
                              venue_name: ddCodeInfo.venue_name || '',
                              store_name: ddCodeInfo.store_name || '',
                              address: ddCodeInfo.address || '',
                              pa: ddCodeInfo.pa || '',
                              fa: logFA || deviceInfoLookup[selectedDDCode]?.fa || ''
                            });
                            navigate(`/new-log?${params.toString()}`);
                          }}
                          size="sm"
                          className="bg-white/20 hover:bg-white/30 text-white border-white/30 backdrop-blur-sm"
                          data-testid="add-log-for-dd-btn"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Log
                        </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-white/80 text-sm">{ddCodeInfo.store_name !== "Unknown" ? ddCodeInfo.store_name : (deviceInfoLookup[selectedDDCode]?.storeName || ddCodeInfo.store_name)}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Venue Name</span>
                  <p className="text-sm font-medium text-slate-900 mt-1 truncate" title={ddCodeInfo.venue_name !== "Unknown" ? ddCodeInfo.venue_name : (deviceInfoLookup[selectedDDCode]?.storeName || "Unknown")}>
                    {ddCodeInfo.venue_name !== "Unknown" ? ddCodeInfo.venue_name : (deviceInfoLookup[selectedDDCode]?.storeName || "Unknown")}
                  </p>
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Store Name</span>
                  <p className="text-sm font-medium text-slate-900 mt-1 truncate" title={ddCodeInfo.store_name !== "Unknown" ? ddCodeInfo.store_name : (deviceInfoLookup[selectedDDCode]?.storeName || "Unknown")}>
                    {ddCodeInfo.store_name !== "Unknown" ? ddCodeInfo.store_name : (deviceInfoLookup[selectedDDCode]?.storeName || "Unknown")}
                  </p>
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">PA</span>
                  <p className="text-sm font-medium text-slate-900 mt-1 truncate" title={ddCodeInfo.pa || deviceInfoLookup[selectedDDCode]?.pa || "—"}>
                    {ddCodeInfo.pa || deviceInfoLookup[selectedDDCode]?.pa || "—"}
                  </p>
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">FA</span>
                  <p className="text-sm font-medium text-slate-900 mt-1 truncate" title={(logs && logs.length > 0 && logs[0].fa) || ddCodeInfo.fa || deviceInfoLookup[selectedDDCode]?.fa || "—"}>
                    {/* Priority: 1. FA from most recent log, 2. ddCodeInfo, 3. CSV lookup, 4. dash */}
                    {(logs && logs.length > 0 && logs[0].fa) || ddCodeInfo.fa || deviceInfoLookup[selectedDDCode]?.fa || "—"}
                  </p>
                </div>
                <div className="min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Stick Number</span>
                  <p className="text-sm font-medium text-slate-900 mt-1 truncate" title={(logs && logs.length > 0 && logs[0].stick_number) || ddCodeInfo.stick_number || deviceInfoLookup[selectedDDCode]?.serialNumber || "—"}>
                    {(logs && logs.length > 0 && logs[0].stick_number) || ddCodeInfo.stick_number || deviceInfoLookup[selectedDDCode]?.serialNumber || "—"}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Address</span>
                  <p className="text-sm font-medium text-slate-900 mt-1 flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                    {(ddCodeInfo.address && ddCodeInfo.address !== "No address found") ? ddCodeInfo.address : (deviceInfoLookup[selectedDDCode]?.address || ddCodeInfo.address)}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Wifi className="w-3 h-3" /> Network
                  </span>
                  <div className="mt-1">
                    {(() => {
                      const latestLog = logs.find(l => l.network_type);
                      const latestWifi = logs.find(l => l.wifi_name || l.wifi_password);
                      if (latestLog?.network_type) {
                        const getNetworkIcon = (type) => {
                          switch(type) {
                            case 'wifi': return <Wifi className="w-3.5 h-3.5 text-blue-500" />;
                            case 'ethernet': return <Cable className="w-3.5 h-3.5 text-green-500" />;
                            case 'lte': return <Signal className="w-3.5 h-3.5 text-orange-500" />;
                            default: return null;
                          }
                        };
                        return (
                          <div className="text-sm">
                            <p className="font-medium text-slate-900 uppercase flex items-center gap-1.5">
                              {getNetworkIcon(latestLog.network_type)}
                              {latestLog.network_type}
                            </p>
                            {latestLog.network_type === 'wifi' && latestLog.wifi_autoconnect && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Autoconnect: <span className={latestLog.wifi_autoconnect === 'yes' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>{latestLog.wifi_autoconnect.toUpperCase()}</span>
                              </p>
                            )}
                            {latestLog.network_type === 'wifi' && latestWifi && latestWifi.wifi_name && (
                              <div className="mt-1 pt-1 border-t border-slate-100">
                                <p className="text-slate-700">{latestWifi.wifi_name}</p>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-slate-500 text-xs">Password:</span>
                                  <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                                    {visiblePasswords['venue'] ? latestWifi.wifi_password : "••••••••"}
                                  </span>
                                  {latestWifi.wifi_password && (
                                    <button
                                      onClick={() => togglePasswordVisibility('venue')}
                                      className="p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
                                    >
                                      {visiblePasswords['venue'] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      return <p className="text-sm text-slate-400">Not set</p>;
                    })()}
                  </div>
                </div>
                {/* Software Requirements */}
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Latest APK</span>
                  <div className="mt-1">
                    {(() => {
                      const latestLog = logs.find(l => l.latest_apk);
                      if (latestLog?.latest_apk) {
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            latestLog.latest_apk === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {latestLog.latest_apk.toUpperCase()}
                          </span>
                        );
                      }
                      return <p className="text-sm text-slate-400">Not set</p>;
                    })()}
                  </div>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Kiosk Mode</span>
                  <div className="mt-1">
                    {(() => {
                      const latestLog = logs.find(l => l.kiosk_mode);
                      if (latestLog?.kiosk_mode) {
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            latestLog.kiosk_mode === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {latestLog.kiosk_mode.toUpperCase()}
                          </span>
                        );
                      }
                      return <p className="text-sm text-slate-400">Not set</p>;
                    })()}
                  </div>
                </div>
              </div>

              <div className="px-6 pb-6 pt-4 border-t border-slate-100">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Owner Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div className="min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Owner's Name</span>
                    <p className="text-sm font-medium text-slate-900 mt-1 truncate">
                      {(logs.length > 0 && logs[0].owner_name) || ddCodeInfo.owner_name || deviceInfoLookup[selectedDDCode]?.partnerName || "—"}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Contact Number</span>
                    <p className="text-sm font-medium text-slate-900 mt-1 truncate">
                      {(logs.length > 0 && logs[0].contact_number) || ddCodeInfo.contact_number || deviceInfoLookup[selectedDDCode]?.phoneNumber || "—"}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email Address</span>
                    <p className="text-sm font-medium text-slate-900 mt-1 truncate">
                      {(() => {
                        const email = (logs.length > 0 && logs[0].owner_email) || ddCodeInfo.owner_email || deviceInfoLookup[selectedDDCode]?.email;
                        return email ? (
                          <a href={`mailto:${email}`} className="text-[#006994] hover:underline">{email}</a>
                        ) : "—";
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Clock className="w-5 h-5 text-[#006994]" />
                Activity Timeline
                <span className="text-sm font-normal text-slate-500">({logs.length} entries)</span>
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={`rounded-lg ${showFilters ? 'bg-slate-100' : ''}`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-2 w-2 h-2 rounded-full bg-[#006994]"></span>
                )}
              </Button>
            </div>

            {showFilters && (
              <div className="dolphin-card p-5 mb-6 animate-fade-in-up">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-slate-700">Filter Timeline</span>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-slate-500 hover:text-slate-700 h-8"
                      data-testid="clear-filters-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {/* Date Filter */}
                  <Select
                    value={filters.date}
                    onValueChange={(value) => handleFilterChange("date", value === "all" ? "" : value)}
                  >
                    <SelectTrigger data-testid="filter-date" className="h-10 text-sm">
                      <SelectValue placeholder="Date" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">All dates</SelectItem>
                      {filterOptions.dates.map((date) => (
                        <SelectItem key={date} value={date}>{date}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.category}
                    onValueChange={(value) => handleFilterChange("category", value === "all" ? "" : value)}
                  >
                    <SelectTrigger data-testid="filter-category" className="h-10 text-sm">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">All categories</SelectItem>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.installer}
                    onValueChange={(value) => handleFilterChange("installer", value === "all" ? "" : value)}
                  >
                    <SelectTrigger data-testid="filter-installer" className="h-10 text-sm">
                      <SelectValue placeholder="Installer" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">All installers</SelectItem>
                      {filterOptions.installers.map((inst) => (
                        <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.dolphin_manager}
                    onValueChange={(value) => handleFilterChange("dolphin_manager", value === "all" ? "" : value)}
                  >
                    <SelectTrigger data-testid="filter-manager" className="h-10 text-sm">
                      <SelectValue placeholder="Manager" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">All managers</SelectItem>
                      {filterOptions.dolphin_managers.map((mgr) => (
                        <SelectItem key={mgr} value={mgr}>{mgr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="filter-date-from"
                        className={`h-10 justify-start text-left font-normal text-sm ${!filters.date_from && "text-slate-400"}`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.date_from ? format(filters.date_from, "PP") : "From"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.date_from}
                        onSelect={(date) => {
                          handleFilterChange("date_from", date);
                          setDateFromOpen(false);
                        }}
                        disabled={(date) => filters.date_to ? date > filters.date_to : false}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="filter-date-to"
                        className={`h-10 justify-start text-left font-normal text-sm ${!filters.date_to && "text-slate-400"}`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.date_to ? format(filters.date_to, "PP") : "To"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.date_to}
                        onSelect={(date) => {
                          handleFilterChange("date_to", date);
                          setDateToOpen(false);
                        }}
                        disabled={(date) => filters.date_from ? date < filters.date_from : false}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}

            {/* Timeline */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-10 h-10 border-3 border-slate-200 border-t-[#006994] rounded-full animate-spin"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="dolphin-card py-16 text-center">
                <div className="w-16 h-16 ocean-gradient rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-[#006994]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  No Service Logs Found
                </h3>
                <p className="text-slate-500">No log entries found for this DD Code.</p>
                {deviceInfoLookup[selectedDDCode] && (
                  <p className="text-xs text-slate-400 mt-2">Device information shown above is from analytics data.</p>
                )}
              </div>
            ) : (
              <div className="relative pl-8">
                <div className="timeline-line"></div>
                {logs.map((log, index) => renderLogCard(log, index, false))}
              </div>
            )}
          </div>
        )}

        {!selectedDDCode && !hasActiveTopFilters && (
          <div className="max-w-lg mx-auto text-center py-16 animate-fade-in-up">
            <div className="w-24 h-24 ocean-gradient rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-12 h-12 text-[#006994]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Search for a DD_Code or Apply Filters
            </h3>
            <p className="text-slate-500 leading-relaxed">
              Enter a DD_Code, venue name, store name, or address in the search bar, or use the filters above to find offline screen activities by date, category, installer, or manager.
            </p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#006994]" />
              Edit Log Entry
            </DialogTitle>
          </DialogHeader>
          
          {editingLog && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <span className="font-medium text-slate-700">{editingLog.dd_code}</span>
                <span className="text-slate-400 mx-2">•</span>
                <span className="text-slate-500">{editingLog.date}</span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-category" className="text-sm font-medium">Category</Label>
                  <Select
                    value={editForm.category}
                    onValueChange={(value) => handleEditFormChange("category", value)}
                  >
                    <SelectTrigger id="edit-category" data-testid="edit-category" className="mt-1.5">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit-installer" className="text-sm font-medium">Installer</Label>
                  <Input
                    id="edit-installer"
                    data-testid="edit-installer"
                    value={editForm.installer}
                    onChange={(e) => handleEditFormChange("installer", e.target.value)}
                    placeholder="Enter installer name..."
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-manager" className="text-sm font-medium">Dolphin Manager</Label>
                  <Input
                    id="edit-manager"
                    data-testid="edit-manager"
                    value={editForm.dolphin_manager}
                    onChange={(e) => handleEditFormChange("dolphin_manager", e.target.value)}
                    placeholder="Enter manager name..."
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-fa" className="text-sm font-medium">FA (Field Agent)</Label>
                  <Input
                    id="edit-fa"
                    data-testid="edit-fa"
                    value={editForm.fa}
                    onChange={(e) => handleEditFormChange("fa", e.target.value)}
                    placeholder="Enter FA name..."
                    className="mt-1.5"
                  />
                </div>

                {/* Owner Contact Information */}
                <div className="border-t pt-4 mt-4">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-[#006994]" />
                    Owner Contact Information
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="edit-owner-name" className="text-xs text-slate-500 mb-1 block">Owner's Name</Label>
                      <Input
                        id="edit-owner-name"
                        data-testid="edit-owner-name"
                        value={editForm.owner_name}
                        onChange={(e) => handleEditFormChange("owner_name", e.target.value)}
                        placeholder="Enter owner's name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-contact-number" className="text-xs text-slate-500 mb-1 block">Contact Number</Label>
                      <Input
                        id="edit-contact-number"
                        data-testid="edit-contact-number"
                        value={editForm.contact_number}
                        onChange={(e) => handleEditFormChange("contact_number", e.target.value)}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-owner-email" className="text-xs text-slate-500 mb-1 block">Email Address</Label>
                      <Input
                        id="edit-owner-email"
                        data-testid="edit-owner-email"
                        type="email"
                        value={editForm.owner_email}
                        onChange={(e) => handleEditFormChange("owner_email", e.target.value)}
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-comments" className="text-sm font-medium">Comments</Label>
                  <Textarea
                    id="edit-comments"
                    data-testid="edit-comments"
                    value={editForm.detailed_comments}
                    onChange={(e) => handleEditFormChange("detailed_comments", e.target.value)}
                    placeholder="Enter detailed comments..."
                    className="mt-1.5 min-h-[100px]"
                  />
                </div>

                <div className="border-t pt-4 mt-4">
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
                    <Wifi className="w-4 h-4 text-[#006994]" />
                    Network Settings
                  </Label>
                  
                  {/* Network Type */}
                  <div className="mb-4">
                    <Label className="text-xs text-slate-500 mb-2 block">Network Type</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditFormChange("network_type", "wifi")}
                        className={`p-2 rounded-lg border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                          editForm.network_type === "wifi"
                            ? "border-[#006994] bg-[#006994]/5 text-[#006994]"
                            : "border-slate-200 hover:border-slate-300 text-slate-500"
                        }`}
                      >
                        <Wifi className="w-4 h-4" />
                        <span className="text-sm font-medium">WiFi</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleEditFormChange("network_type", "ethernet");
                          handleEditFormChange("wifi_autoconnect", "");
                        }}
                        className={`p-2 rounded-lg border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                          editForm.network_type === "ethernet"
                            ? "border-[#006994] bg-[#006994]/5 text-[#006994]"
                            : "border-slate-200 hover:border-slate-300 text-slate-500"
                        }`}
                      >
                        <Cable className="w-4 h-4" />
                        <span className="text-sm font-medium">Ethernet</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleEditFormChange("network_type", "lte");
                          handleEditFormChange("wifi_autoconnect", "");
                        }}
                        className={`p-2 rounded-lg border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                          editForm.network_type === "lte"
                            ? "border-[#006994] bg-[#006994]/5 text-[#006994]"
                            : "border-slate-200 hover:border-slate-300 text-slate-500"
                        }`}
                      >
                        <Signal className="w-4 h-4" />
                        <span className="text-sm font-medium">LTE</span>
                      </button>
                    </div>
                  </div>

                  {editForm.network_type === "wifi" && (
                    <div className="space-y-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100 mb-4">
                      <div>
                        <Label className="text-xs text-slate-500 mb-2 block">WiFi Autoconnect</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditFormChange("wifi_autoconnect", "yes")}
                            className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                              editForm.wifi_autoconnect === "yes"
                                ? "border-green-500 bg-green-50 text-green-700"
                                : "border-slate-200 hover:border-slate-300 text-slate-500"
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditFormChange("wifi_autoconnect", "no")}
                            className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                              editForm.wifi_autoconnect === "no"
                                ? "border-red-500 bg-red-50 text-red-700"
                                : "border-slate-200 hover:border-slate-300 text-slate-500"
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="edit-wifi-name" className="text-xs text-slate-500">WiFi Name</Label>
                          <Input
                            id="edit-wifi-name"
                            data-testid="edit-wifi-name"
                            value={editForm.wifi_name}
                            onChange={(e) => handleEditFormChange("wifi_name", e.target.value)}
                            placeholder="Network name"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-wifi-password" className="text-xs text-slate-500">WiFi Password</Label>
                          <Input
                            id="edit-wifi-password"
                            data-testid="edit-wifi-password"
                            value={editForm.wifi_password}
                            onChange={(e) => handleEditFormChange("wifi_password", e.target.value)}
                            placeholder="Password"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4 mt-4">
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">
                    Software Requirements
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500 mb-2 block">Latest APK</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditFormChange("latest_apk", "yes")}
                          className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                            editForm.latest_apk === "yes"
                              ? "border-green-500 bg-green-50 text-green-700"
                              : "border-slate-200 hover:border-slate-300 text-slate-500"
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditFormChange("latest_apk", "no")}
                          className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                            editForm.latest_apk === "no"
                              ? "border-red-500 bg-red-50 text-red-700"
                              : "border-slate-200 hover:border-slate-300 text-slate-500"
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-2 block">Kiosk Mode</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditFormChange("kiosk_mode", "yes")}
                          className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                            editForm.kiosk_mode === "yes"
                              ? "border-green-500 bg-green-50 text-green-700"
                              : "border-slate-200 hover:border-slate-300 text-slate-500"
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditFormChange("kiosk_mode", "no")}
                          className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                            editForm.kiosk_mode === "no"
                              ? "border-red-500 bg-red-50 text-red-700"
                              : "border-slate-200 hover:border-slate-300 text-slate-500"
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveEdit} 
              disabled={saving}
              className="bg-[#006994] hover:bg-[#005577]"
              data-testid="save-edit-btn"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Log Entry
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this log entry? This action cannot be undone.
            </AlertDialogDescription>
            {deletingLog && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <div><span className="font-medium">DD Code:</span> {deletingLog.dd_code}</div>
                <div><span className="font-medium">Date:</span> {deletingLog.date}</div>
                <div><span className="font-medium">Category:</span> {deletingLog.category}</div>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
